import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

function HeaderMain() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Check if we're on a therapist profile page
  const isTherapistProfilePage = location.pathname.includes('/therapist/');

  useEffect(() => {
    if (!isTherapistProfilePage) return;

    const handleScroll = () => {
      const scrolled = window.scrollY > 200; // Same threshold as ProfileHeader
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isTherapistProfilePage]);

  // Calculate and set header height for ProfileHeader positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header[data-header-main]');
      if (header) {
        const height = header.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    // Update on mount and resize
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [isMobileMenuOpen]); // Recalculate when mobile menu toggles

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 md:py-5 md:mx-[-2rem]" data-header-main>
      <div className="md:px-8">
        <div className="mx-auto max-w-7xl">
        <div 
          className={`
            bg-[#254e70] md:rounded-2xl px-4 md:px-8 py-4 md:py-4 
            border border-[#254e70]/20 
            transition-all duration-300 ease-out
            ${isTherapistProfilePage && isScrolled ? '' : 'md:shadow-xl md:shadow-[#254e70]/30 md:drop-shadow-lg'}
            ${isMobileMenuOpen ? 'rounded-b-none md:shadow-[#254e70]/40' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center transition-all duration-200 hover:opacity-80"
              onClick={closeMobileMenu}
            >
              <img 
                src="http://cms.therapylist.com/wp-content/uploads/2025/07/TherapyList-Logo-Updated.svg"
                alt="TherapyList"
                className="h-12 md:h-16 w-auto max-w-[200px] md:max-w-none"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center justify-between flex-1 pl-8">
              <div className="flex-1 flex justify-center">
                <ul className="flex items-center gap-6 list-none">
                  <li>
                    <Link 
                      to="/find-therapists" 
                      className="text-white/90 font-semibold text-lg font-body px-4 py-2.5 rounded-lg transition-all duration-300 hover:text-white no-underline border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 hover:shadow-lg backdrop-blur-sm"
                    >
                      Find Therapists
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/about" 
                      className="text-white/90 font-semibold text-lg font-body px-4 py-2.5 rounded-lg transition-all duration-300 hover:text-white no-underline border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 hover:shadow-lg backdrop-blur-sm"
                    >
                      About
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/blog" 
                      className="text-white/90 font-semibold text-lg font-body px-4 py-2.5 rounded-lg transition-all duration-300 hover:text-white no-underline border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 hover:shadow-lg backdrop-blur-sm"
                    >
                      Resources
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/for-therapists" 
                      className="text-white/90 font-semibold text-lg font-body px-4 py-2.5 rounded-lg transition-all duration-300 hover:text-white no-underline border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 hover:shadow-lg backdrop-blur-sm"
                    >
                      For Therapists
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/login" 
                      className="text-white/70 font-medium text-lg font-body px-4 py-2.5 rounded-lg transition-all duration-300 hover:text-[#9dacff] no-underline border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 hover:shadow-lg backdrop-blur-sm"
                    >
                      Login
                    </Link>
                  </li>
                </ul>
              </div>
              
              {/* Desktop CTA */}
              <Link 
                to="/join" 
                className="
                  bg-transparent text-[#9dacff] px-8 py-3 rounded-xl 
                  font-bold text-base font-body transition-all duration-300 
                  hover:bg-[#9dacff] hover:text-[#254e70] 
                  border-2 border-[#9dacff] no-underline flex-shrink-0
                  shadow-sm hover:shadow-lg
                "
              >
                Join for Free
              </Link>
            </nav>
            
            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden flex flex-col gap-1 bg-transparent border-none cursor-pointer p-1"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span 
                className={`
                  w-5 h-0.5 bg-white rounded transition-all duration-300
                  ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}
                `}
              ></span>
              <span 
                className={`
                  w-5 h-0.5 bg-white rounded transition-all duration-300
                  ${isMobileMenuOpen ? 'opacity-0' : ''}
                `}
              ></span>
              <span 
                className={`
                  w-5 h-0.5 bg-white rounded transition-all duration-300
                  ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}
                `}
              ></span>
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div 
          className={`
            md:hidden bg-[#254e70] py-4
            border-t border-white/10 transition-all duration-300
            ${isTherapistProfilePage && isScrolled ? '' : 'md:shadow-xl md:shadow-[#254e70]/30 md:drop-shadow-lg'}
            ${isMobileMenuOpen ? 'block' : 'hidden'}
            flex justify-center
          `}
        >
          <div className="w-full max-w-[240px]">
            <ul className="flex flex-col gap-2.5 list-none items-center">
              <li>
                <Link 
                  to="/find-therapists" 
                  className="text-white/90 font-semibold text-base font-body px-3 py-3 rounded transition-all duration-300 hover:text-white block no-underline text-center border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 backdrop-blur-sm"
                  onClick={closeMobileMenu}
                >
                  Find Therapists
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="text-white/90 font-semibold text-base font-body px-3 py-3 rounded transition-all duration-300 hover:text-white block no-underline text-center border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 backdrop-blur-sm"
                  onClick={closeMobileMenu}
                >
                  About
                </Link>
              </li>
              <li>
                <Link 
                  to="/blog" 
                  className="text-white/90 font-semibold text-base font-body px-3 py-3 rounded transition-all duration-300 hover:text-white block no-underline text-center border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 backdrop-blur-sm"
                  onClick={closeMobileMenu}
                >
                  Resources
                </Link>
              </li>
              <li>
                <Link 
                  to="/for-therapists" 
                  className="text-white/90 font-semibold text-base font-body px-3 py-3 rounded transition-all duration-300 hover:text-white block no-underline text-center border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 backdrop-blur-sm"
                  onClick={closeMobileMenu}
                >
                  For Therapists
                </Link>
              </li>
              <li>
                <Link 
                  to="/login" 
                  className="text-white/70 font-medium text-base font-body px-3 py-3 rounded transition-all duration-300 hover:text-[#9dacff] block no-underline text-center border border-transparent hover:border-[#9dacff]/30 hover:bg-gradient-to-r hover:from-[#9dacff]/20 hover:to-[#9dacff]/10 backdrop-blur-sm"
                  onClick={closeMobileMenu}
                >
                  Login
                </Link>
              </li>
            </ul>
            
            {/* Mobile CTA */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex justify-center mb-3">
                <Link 
                  to="/join" 
                  className="
                    bg-[#9dacff] text-white px-8 py-3.5 rounded-xl 
                    font-bold text-lg font-body transition-all duration-300 
                    hover:bg-white hover:text-[#9dacff] 
                    border-2 border-[#9dacff] text-center no-underline
                    shadow-lg hover:shadow-xl w-full max-w-[200px]
                  "
                  onClick={closeMobileMenu}
                >
                  Join for Free
                </Link>
              </div>
              <div className="text-center">
                <p className="text-white/60 text-sm">
                  Already have an account?{' '}
                  <Link 
                    to="/login" 
                    className="text-[#9dacff] hover:text-white font-medium transition-colors duration-200"
                    onClick={closeMobileMenu}
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </header>
  );
}

export default HeaderMain; 